<template>
  <div v-if="open" class="conversation-members-dropdown" @click.stop>
    <div class="conversation-members-dropdown-head">
      <span>{{ $t("conversation-members-title") }}</span>
      <span class="conversation-members-dropdown-count">
        {{ toPersianDigits(members.length) }}
      </span>
    </div>

    <div class="conversation-members-add">
      <autocomplete
        v-model="selectedUser"
        title="conversation-add-member"
        type="partners"
        :filter="false"
        :multiple="false"
        :value="selectedUser"
        :disabled="busy"
      />
    </div>

    <div class="conversation-members-list">
      <div v-if="loading" class="conversation-members-hint is-compact">
        <v-progress-circular indeterminate size="18" width="2" color="#0066FF" />
      </div>
      <div
        v-for="item in members"
        :key="memberKey(item)"
        class="conversation-members-item"
      >
        <div
          class="conversation-members-avatar"
          :style="{ background: avatarColor(memberUserId(item)) }"
        >
          <img
            v-if="memberAvatarUrl(item)"
            :src="memberAvatarUrl(item)"
            :alt="memberDisplayName(item)"
          />
          <span v-else>{{ titleInitial(memberDisplayName(item)) }}</span>
        </div>
        <div class="conversation-members-meta">
          <div class="conversation-members-name">
            {{ memberDisplayName(item) }}
          </div>
          <div v-if="isOwner(item)" class="conversation-members-role">
            {{ $t("conversation-owner") }}
          </div>
        </div>
        <v-tooltip v-if="!isOwner(item)" bottom>
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="conversation-members-remove"
              v-bind="attrs"
              v-on="on"
              :disabled="busy"
              @click="$emit('remove', item)"
            >
              <v-icon size="14" color="#ef4444">mdi-close</v-icon>
            </button>
          </template>
          <span>{{ $t("delete") }}</span>
        </v-tooltip>
      </div>
      <div v-if="!loading && !members.length" class="conversation-members-hint is-compact">
        {{ $t("no-data-found") }}
      </div>
    </div>
  </div>
</template>

<script>
import autocomplete from "../patial/autocomplete.vue";

export default {
  name: "ConversationMembers",
  components: {
    autocomplete,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    members: {
      type: Array,
      default() {
        return [];
      },
    },
    ownerId: {
      type: [Number, String],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedUser: null,
      clearingSelection: false,
    };
  },
  watch: {
    open(val) {
      if (val) {
        this.selectedUser = null;
        document.addEventListener("click", this.onDocumentClick, true);
      } else {
        this.selectedUser = null;
        document.removeEventListener("click", this.onDocumentClick, true);
      }
    },
    selectedUser(val) {
      if (this.clearingSelection) {
        return;
      }
      if (!val) {
        return;
      }
      var user =
        typeof val === "object"
          ? val
          : { id: val };
      if (user.id == null || this.isAlreadyMember(user.id)) {
        this.clearSelectedUser();
        return;
      }
      this.$emit("add", user);
      this.clearSelectedUser();
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick, true);
  },
  methods: {
    clearSelectedUser() {
      var self = this;
      this.clearingSelection = true;
      this.selectedUser = null;
      this.$nextTick(function () {
        self.clearingSelection = false;
      });
    },
    onDocumentClick(event) {
      if (!this.open) {
        return;
      }
      var root = this.$el;
      if (root && root.contains && root.contains(event.target)) {
        return;
      }
      var toggle =
        event.target && event.target.closest
          ? event.target.closest("[data-members-toggle]")
          : null;
      if (toggle) {
        return;
      }
      // Autocomplete menu / mobile dialog often render outside this panel
      var outsideUi =
        event.target && event.target.closest
          ? event.target.closest(
              ".v-menu__content, .v-dialog, .v-overlay, .v-list"
            )
          : null;
      if (outsideUi) {
        return;
      }
      this.$emit("close");
    },
    avatarColor(id) {
      var colors = [
        "#0066FF",
        "#00897B",
        "#7B61FF",
        "#FF6B35",
        "#E91E63",
        "#FF9800",
        "#4CAF50",
      ];
      var n = Number(id) || 0;
      if (!n) {
        return colors[0];
      }
      return colors[Math.abs(n) % colors.length];
    },
    titleInitial(title) {
      return title ? String(title).charAt(0) : "?";
    },
    toPersianDigits(value) {
      if (value == null) {
        return "";
      }
      return String(value).replace(/\d/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
    },
    formatName(user) {
      if (!user) {
        return "";
      }
      if (user.name) {
        return user.name;
      }
      var parts = [user.firstname, user.lastname].filter(Boolean);
      return parts.join(" ").trim();
    },
    memberUser(item) {
      if (!item) {
        return null;
      }
      if (item.user && typeof item.user === "object") {
        return item.user;
      }
      if (
        item.user_id == null &&
        (item.name != null || item.firstname != null || item.uid != null)
      ) {
        return item;
      }
      return null;
    },
    memberUserId(item) {
      if (!item) {
        return null;
      }
      if (item.user_id != null) {
        return item.user_id;
      }
      if (item.user && item.user.id != null) {
        return item.user.id;
      }
      if (
        item.name != null ||
        item.firstname != null ||
        item.uid != null
      ) {
        return item.id != null ? item.id : null;
      }
      return null;
    },
    memberDisplayName(item) {
      var name = this.formatName(this.memberUser(item));
      if (name) {
        return name;
      }
      var uid = this.memberUserId(item);
      return uid != null ? "#" + uid : "";
    },
    memberAvatarUrl(item) {
      var user = this.memberUser(item);
      if (!user) {
        return "";
      }
      var avatar = user.avatar;
      if (avatar && typeof avatar === "object") {
        return avatar.link || avatar.url || avatar.path || "";
      }
      if (typeof avatar === "string" && avatar) {
        return avatar;
      }
      var nested =
        user.avatar_url ||
        user.photo ||
        user.image ||
        user.profile_image ||
        null;
      if (nested && typeof nested === "object") {
        return nested.link || nested.url || nested.path || "";
      }
      return nested || "";
    },
    memberKey(item) {
      var uid = this.memberUserId(item);
      if (uid != null) {
        return "u-" + uid;
      }
      return item && item.id != null ? "a-" + item.id : String(item);
    },
    isOwner(item) {
      if (this.ownerId == null || !item) {
        return false;
      }
      return Number(this.memberUserId(item)) === Number(this.ownerId);
    },
    isAlreadyMember(userId) {
      var list = this.members || [];
      for (var i = 0; i < list.length; i++) {
        if (Number(this.memberUserId(list[i])) === Number(userId)) {
          return true;
        }
      }
      return false;
    },
  },
};
</script>

<style scoped>
.conversation-members-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 300px;
  max-height: 380px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  z-index: 30;
  direction: rtl;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.conversation-members-dropdown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111;
  border-bottom: 1px solid #f0f0f0;
  flex: 0 0 auto;
}
.conversation-members-dropdown-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8f1ff;
  color: #0066ff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  font-weight: 600;
}
.conversation-members-add {
  padding: 2px 10px 22px;
  border-bottom: 1px solid #f3f4f6;
  flex: 0 0 auto;
  overflow: visible;
}
.conversation-members-add ::v-deep .v-input {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}
.conversation-members-add ::v-deep .v-text-field__details {
  display: none !important;
}
.conversation-members-add ::v-deep .v-input__slot {
  min-height: 36px !important;
}
.conversation-members-add ::v-deep fieldset {
  top: 0 !important;
}
.conversation-members-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0 6px;
}
.conversation-members-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
}
.conversation-members-item:hover {
  background: #f8fafc;
}
.conversation-members-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex: 0 0 auto;
  overflow: hidden;
}
.conversation-members-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-members-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.conversation-members-name {
  font-size: 12px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-members-role {
  font-size: 10px;
  color: #6b7280;
  margin-top: 1px;
}
.conversation-members-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.conversation-members-remove:hover:not(:disabled) {
  background: #fef2f2;
}
.conversation-members-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 10px;
  color: #9aa0a6;
  font-size: 12px;
}
.conversation-members-hint.is-compact {
  padding: 10px;
}
</style>
